import { makeStyles } from '@mui/styles';
import React from 'react';
import { useAppSelector } from '../../app/hooks';
import { TranscriptTypes } from '../../constants/enums';
import {
  EntitySpan,
  TextFrameTransmissionMessage,
} from '../../reducers/messagingSlice';
import { selectEntityAnnotationsMap } from '../../selectors/message';
import { LOWER_CASE_O, OH } from '../../utils/constants';
import { TranscriptMessage } from '../TranscriptMessage';

// secondary_asr (HITL-1497): a customer line sourced from an `asr_promoted_text`
// event is normalized into frame shape by the transcript selector, which sets
// `isPromoted` from that event's own `metadata.source`. There is no matching or
// lookup here any more — the flag is applied upstream, so this component renders
// whatever list it is handed.
export type TranscriptFrame = TextFrameTransmissionMessage & {
  isPromoted?: boolean;
};

interface OwnProps {
  messageList: TranscriptFrame[];
  // Forwarded to TranscriptMessage; the HITL 3.0 transcript card renders denser
  // messages. Defaults false → original layout.
  compact?: boolean;
}

const useStyles = makeStyles(({}) => ({
  bottomSpacer: {
    paddingBottom: '10px',
  },
}));

// HITL-1481: this component renders the list and nothing else — it does NOT own
// a scroll container (the parent's `overflow-y: auto` element does), so the
// "stick to bottom" behaviour lives there via useStickyScroll. It used to scroll
// from here against its own auto-height div, which always measured as "at the
// bottom"; and with two of these inside one scroll parent (session buffer + live
// list) they fought over the same scroll position.
export function MessageContainer({ messageList, compact = false }: OwnProps) {
  const { bottomSpacer } = useStyles();
  const entityAnnotationsMap = useAppSelector(selectEntityAnnotationsMap);

  const renderMessage = (message: TranscriptFrame) => {
    let payload = message.data?.payload;
    const isBlocked = message.data?.blocked_status?.blocked === true;
    const blockedReason = message.data?.blocked_status?.reason;
    // secondary_asr: keyed by the rendered frame's own id. For a promoted-source
    // line that is the `asr_promoted_text` event id, which is what
    // `transcript_highlight.source_text_event_id` points at — so this resolves
    // without any join.
    const annotations: EntitySpan[] | undefined =
      entityAnnotationsMap?.[message.id];
    if (message.data && message.agent_type == 'ORDERBOARD' && payload) {
      if (message.data.status !== 'TTS') {
        return (
          <TranscriptMessage
            key={message.id}
            text={payload}
            secondLangText={message.metadata?.second_lang_text}
            type={TranscriptTypes.CX}
            isBlocked={isBlocked}
            blockedReason={blockedReason}
            entityAnnotations={annotations}
            isPromoted={message.isPromoted === true}
            compact={compact}
            timestamp={message.timestamp}
          />
        );
      }

      const regularExpr = /\d+\sOh\s\d+/;
      if (regularExpr.exec(payload)) {
        payload = payload.replace(OH, LOWER_CASE_O); // Display 'Oh' in the total tts visually as 'O'
      }
      return (
        <TranscriptMessage
          key={message.id}
          text={payload}
          secondLangText={message.metadata?.second_lang_text}
          type={TranscriptTypes.AI}
          isBlocked={isBlocked}
          blockedReason={blockedReason}
          compact={compact}
          timestamp={message.timestamp}
        />
      );
    }
    // null, not an empty fragment: both render nothing, but a fragment is an
    // element and inside the map() below it would need a key — which is what
    // produced React's "unique key" warning for every non-ORDERBOARD frame.
    return null;
  };

  return (
    <div data-testid="messageContainer">
      {messageList.map((message) => renderMessage(message))}
      <div className={bottomSpacer} />
    </div>
  );
}
